import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { QRCodeSVG } from 'qrcode.react';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import Litepicker from '@/components/Base/Litepicker';
import Chart from '@/components/Base/Chart';
import { Popover } from '@/components/Base/Headless';
import {
  fetchStaffDuitNowQrBlob,
  fetchStaffPerformance,
  type StaffPerformance,
  type StaffRow,
} from '@/api/staff';
import { buildStaffSurveyQrUrl } from '@/config/surveyUrl';

const DATE_STORAGE_KEY = 'ldsurvey:admin:ambassadorDetailDateRange';
const BRAND_COLORS = ['#1e3a8a', '#2563eb', '#38bdf8', '#f97316', '#eab308', '#94a3b8'];

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function defaultMonthRange(): string {
  const start = dayjs().startOf('month').format('YYYY-MM-DD');
  const end = dayjs().endOf('month').format('YYYY-MM-DD');
  return `${start} - ${end}`;
}

function toIsoRange(range: string) {
  const raw = (range || '').trim();
  if (!raw) return null;
  const [startRaw, endRaw] = raw.split(' - ');
  const startStr = (startRaw || '').trim();
  const endStr = ((endRaw || '') || startStr).trim();
  if (!startStr) return null;
  const start = dayjs(startStr);
  const end = dayjs(endStr);
  if (!start.isValid() || !end.isValid()) return null;
  return {
    startISO: start.startOf('day').toISOString(),
    endISO: end.endOf('day').toISOString(),
  };
}

function formatRelativeTime(iso: string): string {
  const then = dayjs(iso);
  if (!then.isValid()) return '';
  const diffMin = dayjs().diff(then, 'minute');
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = dayjs().diff(then, 'hour');
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = dayjs().diff(then, 'day');
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return then.format('DD MMM YYYY');
}

function formatStatus(staff: StaffRow): { label: string; className: string } {
  if (!staff.isActive) {
    return { label: 'Deactivated', className: 'bg-slate-100 text-slate-600' };
  }
  if (!staff.isRegistered) {
    return { label: 'Pending', className: 'bg-amber-100 text-amber-700' };
  }
  return { label: 'Active', className: 'bg-emerald-100 text-emerald-700' };
}

function ChangeBadge({ value, suffix = '% vs last period' }: { value: number; suffix?: string }) {
  const positive = value >= 0;
  return (
    <p className={`mt-1 text-xs font-medium ${positive ? 'text-emerald-600' : 'text-rose-600'}`}>
      {positive ? '↑' : '↓'} {Math.abs(value)}%{suffix ? ` ${suffix}` : ''}
    </p>
  );
}

function ConversionRateInfo() {
  return (
    <Popover className="inline-flex">
      <Popover.Button
        as="button"
        type="button"
        className="inline-flex items-center justify-center rounded-full text-slate-400 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        aria-label="How conversion rate is calculated"
      >
        <Lucide icon="Info" className="w-3.5 h-3.5 stroke-[2]" />
      </Popover.Button>
      <Popover.Panel placement="bottom-end" className="w-64 p-3 text-left normal-case">
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
          <span className="font-medium">Unique Consumers</span> ÷{' '}
          <span className="font-medium">QR Scans</span> × 100
        </p>
        <ul className="mt-2 space-y-1 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
          <li>Unique Consumers — distinct phone numbers with a completed survey in this period</li>
          <li>QR Scans — times the survey was opened via this ambassador&apos;s QR link</li>
        </ul>
      </Popover.Panel>
    </Popover>
  );
}

function MetricCard({
  title,
  value,
  change,
  changeSuffix,
  titleExtra,
}: {
  title: string;
  value: string;
  change?: number;
  changeSuffix?: string;
  titleExtra?: React.ReactNode;
}) {
  return (
    <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-5 py-4 shadow-sm">
      <div className="flex items-center gap-1">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</p>
        {titleExtra}
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-800 dark:text-slate-100 tabular-nums">{value}</p>
      {change !== undefined && <ChangeBadge value={change} suffix={changeSuffix} />}
    </div>
  );
}

function activityIcon(type: string) {
  if (type === 'qr') {
    return { icon: 'QrCode' as const, className: 'bg-emerald-100 text-emerald-600' };
  }
  return { icon: 'ClipboardCheck' as const, className: 'bg-primary/10 text-primary' };
}

function DuitNowQrPanel({
  staffId,
  hasDuitNowQr,
  pending,
}: {
  staffId: string;
  hasDuitNowQr: boolean;
  pending?: boolean;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(hasDuitNowQr || Boolean(pending));

  useEffect(() => {
    if (pending) {
      setLoading(true);
      return;
    }

    if (!hasDuitNowQr) {
      setSrc(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | undefined;
    setLoading(true);

    void fetchStaffDuitNowQrBlob(staffId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [staffId, hasDuitNowQr, pending]);

  return (
    <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 overflow-hidden shadow-sm bg-gradient-to-br from-theme-1 to-theme-2">
      <div className="p-5">
        <h3 className="text-sm font-semibold text-white/90">DuitNow QR</h3>
        <div className="mt-4 flex justify-center rounded-xl bg-white p-4">
          {loading ? (
            <div className="flex h-[180px] w-[180px] items-center justify-center">
              <Lucide icon="Loader" className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : src ? (
            <img
              src={src}
              alt="DuitNow QR"
              className="h-[180px] w-[180px] object-contain"
            />
          ) : (
            <div className="flex h-[180px] w-[180px] items-center justify-center text-xs text-slate-400">
              No Image
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

async function downloadQrSvg(svgEl: SVGSVGElement, filename: string) {
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgEl);
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((pngBlob) => {
        if (!pngBlob) return;
        const pngUrl = URL.createObjectURL(pngBlob);
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(pngUrl);
      }, 'image/png');
    }
    URL.revokeObjectURL(url);
  };
  img.src = url;
}

function Main() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qrRef = useRef<SVGSVGElement | null>(null);

  const readInitialRange = () => {
    if (typeof window === 'undefined') return defaultMonthRange();
    const saved = window.localStorage.getItem(DATE_STORAGE_KEY);
    return saved?.trim() || defaultMonthRange();
  };

  const [dateRange, setDateRange] = useState(readInitialRange);
  const [confirmedRange, setConfirmedRange] = useState(() => toIsoRange(readInitialRange()));
  const [staff, setStaff] = useState<StaffRow | null>(null);
  const [performance, setPerformance] = useState<StaffPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const surveyUrl = useMemo(() => buildStaffSurveyQrUrl(staff?.staffCode), [staff?.staffCode]);
  const status = staff ? formatStatus(staff) : null;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const value = dateRange.trim();
    if (value) window.localStorage.setItem(DATE_STORAGE_KEY, value);
  }, [dateRange]);

  const loadPerformance = useCallback(async () => {
    if (!id || !confirmedRange) return;

    setLoading(true);
    setError('');
    try {
      const data = await fetchStaffPerformance(id, confirmedRange);
      setStaff(data.staff);
      setPerformance(data.performance);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load performance');
      setStaff(null);
      setPerformance(null);
    } finally {
      setLoading(false);
    }
  }, [id, confirmedRange]);

  useEffect(() => {
    void loadPerformance();
  }, [loadPerformance]);

  useEffect(() => {
    if (!staff?.name || !id) return;
    const state = location.state as { ambassadorName?: string } | null;
    if (state?.ambassadorName === staff.name) return;
    navigate(location.pathname, {
      replace: true,
      state: { ambassadorName: staff.name },
    });
  }, [staff?.name, id, location.pathname, location.state, navigate]);

  const handleApplyRange = () => {
    const iso = toIsoRange(dateRange);
    if (!iso) {
      setError('Please select a valid date range');
      return;
    }
    setConfirmedRange(iso);
  };

  const handleDownloadQr = () => {
    if (!qrRef.current || !staff?.staffCode) return;
    void downloadQrSvg(qrRef.current, `${staff.staffCode}-qr.png`);
  };

  const handleShareQr = async () => {
    if (!surveyUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${staff?.name ?? 'Ambassador'} — Survey QR`,
          text: 'Scan to complete the survey',
          url: surveyUrl,
        });
      } catch {
        /* user cancelled */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(surveyUrl);
    } catch {
      /* ignore */
    }
  };

  const trendChart = useMemo(() => {
    const labels = performance?.surveyTrend.map((row) => row.date) ?? [];
    const data = performance?.surveyTrend.map((row) => row.count) ?? [];
    return {
      type: 'line' as const,
      data: {
        labels,
        datasets: [
          {
            label: 'Surveys',
            data,
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37, 99, 235, 0.08)',
            tension: 0.45,
            cubicInterpolationMode: 'monotone' as const,
            fill: true,
            pointRadius: 4,
            pointBackgroundColor: '#2563eb',
            pointHoverRadius: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, datalabels: { display: false } },
        scales: {
          x: { grid: { display: false } },
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    };
  }, [performance?.surveyTrend]);

  const brandChart = useMemo(() => {
    const brands = performance?.topBrands ?? [];
    const total = brands.reduce((sum, row) => sum + row.count, 0);
    const labels = brands.map((row) => row.brand);
    const data = brands.map((row) => row.count);
    const legend = brands.map((row, index) => ({
      label: row.brand,
      count: row.count,
      color: BRAND_COLORS[index % BRAND_COLORS.length],
      pct: total > 0 ? Math.round((row.count / total) * 100) : 0,
    }));
    return {
      total,
      legend,
      config: {
        type: 'doughnut' as const,
        data: {
          labels,
          datasets: [
            {
              data,
              backgroundColor: BRAND_COLORS.slice(0, labels.length),
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '68%',
          plugins: {
            legend: { display: false },
            datalabels: { display: false },
          },
        },
      },
    };
  }, [performance?.topBrands]);

  const weeklyChart = useMemo(() => {
    const rows = performance?.weeklyPerformance ?? [];
    return {
      type: 'bar' as const,
      data: {
        labels: rows.map((row) => row.week),
        datasets: [
          {
            label: 'Surveys',
            data: rows.map((row) => row.surveys),
            backgroundColor: '#2563eb',
            borderRadius: 4,
            barThickness: 12,
          },
          {
            label: 'QR Scans',
            data: rows.map((row) => row.qrScans),
            backgroundColor: '#10b981',
            borderRadius: 4,
            barThickness: 12,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 8 },
        },
        plugins: {
          datalabels: {
            anchor: 'end' as const,
            align: 'top' as const,
            color: '#64748b',
            font: { size: 10 },
            clip: false,
            offset: 2,
          },
        },
        datasets: {
          bar: {
            barPercentage: 1,
            categoryPercentage: 0.13,
          },
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            beginAtZero: true,
            grace: '15%',
            ticks: { precision: 0 },
          },
        },
      },
    };
  }, [performance?.weeklyPerformance]);

  if (!id) {
    return (
      <div className="p-6">
        <p className="text-danger">Invalid ambassador ID</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
        <div className="relative w-full sm:w-auto sm:min-w-[280px]">
          <Lucide
            icon="Calendar"
            className="absolute left-3 top-1/2 z-10 w-4 h-4 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <Litepicker
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            options={{
              format: 'YYYY-MM-DD',
              singleMode: false,
              numberOfColumns: 2,
              numberOfMonths: 2,
              autoApply: false,
            }}
            className="pl-10 pr-4 py-2.5 w-full rounded-lg border border-slate-200/80 dark:border-darkmode-400 bg-white dark:bg-darkmode-600 text-sm"
          />
        </div>
        <Button type="button" variant="primary" onClick={handleApplyRange}>
          Apply
        </Button>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[280px_1fr] xl:items-stretch">
          <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-5 shadow-sm flex flex-col h-full">
            {loading && !staff ? (
              <div className="py-8 text-center text-slate-400">
                <Lucide icon="Loader" className="w-5 h-5 mx-auto mb-2 animate-spin" />
                Loading…
              </div>
            ) : staff ? (
              <>
                <div className="flex flex-col items-center text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-xl font-bold text-primary">
                    {getInitials(staff.name)}
                  </div>
                  <h2 className="mt-4 text-lg font-bold text-slate-800 dark:text-slate-100">
                    {staff.name}
                  </h2>
                  <p className="text-sm text-slate-500">Internal Ambassador</p>
                </div>
                <dl className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Membership Code</dt>
                    <dd className="font-medium text-slate-800 dark:text-slate-100">
                      {staff.memberCode || '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Region</dt>
                    <dd className="font-medium text-slate-800 dark:text-slate-100">—</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">State</dt>
                    <dd className="font-medium text-slate-800 dark:text-slate-100">
                      {performance?.topState || '—'}
                    </dd>
                  </div>
                </dl>
                {status && (
                  <div className="mt-5 flex justify-center">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${status.className}`}
                    >
                      {status.label}
                    </span>
                  </div>
                )}
                <div className="mt-auto pt-5 flex justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline-primary"
                    className="text-xs"
                    onClick={() => navigate(`/ambassador/${id}/edit`)}
                  >
                    <Lucide icon="Pencil" className="w-3.5 h-3.5 mr-1.5" />
                    Edit
                  </Button>
                </div>
              </>
            ) : null}
          </div>

          <div className="flex flex-col gap-5 min-w-0">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                title="Total Points"
                value={performance?.totalPoints != null ? performance.totalPoints.toLocaleString() : '—'}
              />
              <MetricCard
                title="Total Surveys"
                value={loading ? '…' : (performance?.totalSurveys ?? 0).toLocaleString()}
                change={performance?.changes.totalSurveys}
              />
              <MetricCard
                title="Unique Consumers"
                value={loading ? '…' : (performance?.uniqueConsumers ?? 0).toLocaleString()}
                change={performance?.changes.uniqueConsumers}
              />
              <MetricCard
                title="Conversion Rate"
                titleExtra={<ConversionRateInfo />}
                value={loading ? '…' : `${performance?.conversionRate ?? 0}%`}
                change={performance?.changes.conversionRate}
                changeSuffix=" pts vs last period"
              />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-4 shadow-sm flex flex-col h-[340px]">
                <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3">
                  Survey Submission Trend
                </h3>
                <div className="relative flex-1 min-h-0">
                  {performance && performance.surveyTrend.length > 0 ? (
                    <Chart {...trendChart} height="auto" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-400">
                      {loading ? 'Loading chart…' : 'No survey data for this period'}
                    </div>
                  )}
                </div>
              </div>

              <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-4 shadow-sm flex flex-col h-[340px]">
                <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3">
                  Top Brands
                </h3>
                <div className="relative flex-1 min-h-0 flex items-center">
                  {performance && brandChart.total > 0 ? (
                    <>
                      <div className="relative h-full w-[65%] min-w-0">
                        <Chart {...brandChart.config} height="auto" />
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-xl font-bold text-slate-800 dark:text-slate-100 tabular-nums">
                              {brandChart.total}
                            </p>
                            <p className="text-xs text-slate-500">Surveys</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex h-full w-[35%] flex-col justify-center gap-2 pl-2 overflow-y-auto">
                        {brandChart.legend.map((item) => (
                          <div key={item.label} className="flex items-center gap-2 min-w-0">
                            <span
                              className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-xs text-slate-600 dark:text-slate-400 truncate">
                              {item.label}: {item.pct}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-sm text-slate-400">
                      {loading ? 'Loading chart…' : 'No brand data for this period'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[280px_1fr] xl:items-stretch">
          <div className="flex flex-col gap-5">
            <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 overflow-hidden shadow-sm bg-gradient-to-br from-theme-1 to-theme-2">
            <div className="p-5">
              <h3 className="text-sm font-semibold text-white/90">My QR Code</h3>
              <div className="mt-4 flex justify-center rounded-xl bg-white p-4">
                {surveyUrl ? (
                  <QRCodeSVG
                    ref={qrRef}
                    value={surveyUrl}
                    size={180}
                    level="H"
                    includeMargin
                  />
                ) : (
                  <div className="flex h-[180px] w-[180px] items-center justify-center text-xs text-slate-400">
                    QR not available
                  </div>
                )}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline-primary"
                  className="w-full bg-white/10 border-white/40 text-white hover:bg-white/20 text-xs uppercase tracking-wide"
                  disabled={!surveyUrl}
                  onClick={handleDownloadQr}
                >
                  Download
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className="w-full bg-white text-primary hover:bg-white/90 text-xs uppercase tracking-wide border-0"
                  disabled={!surveyUrl}
                  onClick={() => void handleShareQr()}
                >
                  Share QR
                </Button>
              </div>
            </div>
            </div>
            {id ? (
              <DuitNowQrPanel
                staffId={id}
                hasDuitNowQr={Boolean(staff?.hasDuitNowQr)}
                pending={!staff}
              />
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-stretch min-w-0">
            <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-4 shadow-sm flex flex-col h-full min-h-[340px]">
              <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3">
                Recent Activity
              </h3>
              <ul className="space-y-3 flex-1">
                {loading ? (
                  <li className="text-sm text-slate-400 py-4 text-center">Loading…</li>
                ) : performance && performance.recentActivity.length > 0 ? (
                  performance.recentActivity.map((item, index) => {
                    const meta = activityIcon(item.type);
                    return (
                      <li key={`${item.at}-${index}`} className="flex items-start gap-3">
                        <span
                          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${meta.className}`}
                        >
                          <Lucide icon={meta.icon} className="w-4 h-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                            {item.label}
                          </p>
                          <p className="text-xs text-slate-400">{formatRelativeTime(item.at)}</p>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="text-sm text-slate-400 py-4 text-center">No recent activity</li>
                )}
              </ul>
            </div>

            <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-4 shadow-sm flex flex-col h-full min-h-[340px]">
              <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3">
                Performance vs Last Month
              </h3>
              <div className="relative flex-1 min-h-0">
                {performance && performance.weeklyPerformance.length > 0 ? (
                  <Chart {...weeklyChart} height="auto" />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">
                    {loading ? 'Loading chart…' : 'No weekly data for this period'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Main;
